/*
  Per-ROW desktop rails for the cue rows.

  A row keeps being a row. What changes is that a row may now hold more
  tiles than fit across, and when it does it becomes a horizontal rail:
  four cards in view, the next one peeking at the right edge so the row
  visibly continues, and a pair of arrows in the row's header.

  Only rows that overflow are touched. A row of four or fewer stays the
  grid it always was - no track, no arrows, no peek - so nothing changes
  for the rows that never needed it.

  Everything is added by script, so the Liquid carries no rail markup and a
  row that gains a tile in settings_data needs no other change. Phones are
  untouched: this runs at >=750px only and undoes itself below that.
*/
(function () {
  /*
    ONE INSTANCE.

    Three sections now ask for this file - mcdermott-tiles, longoni-cues
    and cloth-rows-host - and a second <script> with the same src executes
    the whole IIFE again rather than being deduped. That gave two module
    instances with two separate `rails` arrays, each scanning the document
    and each wiring its own resize listener. Harmless only because the
    per-element build guards caught the duplicates.
  */
  if (window.BCDesktopRail) return;

  var DESKTOP = '(min-width: 750px)';
  var PER_VIEW = 4;
  var rails = [];
  var wired = false;

  var TILE_DEFAULT = '.longoni-promo-strip__tile';

  /*
    Every tile but the ones a row has taken out because they cannot be
    bought (the homepage's sold-out rule, 2026-09-22): counted, a sold-out
    card made a row of four look like five and built a rail for it, and its
    all-zero rect stood in the snap and pitch sums as a card at the lane's
    left edge.

    Excluded BY THAT MARK, not by "has no box". The first version of this
    filter dropped every tile without a box, and a row that is not yet a
    rail hides its tiles past the view count on purpose
    (assets/bc-responsive.css, :not(.bc-rail-row) ... :nth-child(n+N)) so
    nothing jumps before the rail is built - those tiles were no longer
    counted, the rail was never built, and every long McDermott and Longoni
    row stood at its first three or four tiles. Measured on the preview at
    1280 the same day, fixed the same hour.
  */
  var SOLD_OUT_HIDDEN = '[data-sold-out], .rail-rubber-card--sold-out';

  function tilesOf(el, sel) {
    return Array.prototype.filter.call(el.querySelectorAll(sel || TILE_DEFAULT), function (tile) {
      return !tile.matches(SOLD_OUT_HIDDEN);
    });
  }

  function arrow(dir) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'bc-rail-arrow bc-rail-arrow--' + dir;
    b.setAttribute('aria-label', dir === 'prev' ? 'Scroll left' : 'Scroll right');
    b.innerHTML = '<span aria-hidden="true"></span>';
    return b;
  }

  function Rail(row, section, headerSlot, opts) {
    this.row = row;
    this.section = section;
    this.headerSlot = headerSlot;   // where the arrows go, or null for inline
    this.built = false;
    opts = opts || {};
    /*
      A row that ALREADY scrolls is adopted rather than wrapped.

      The Championship cloth lane is its own scroller: overflow-x:auto,
      scroll-snap-type:x mandatory, cards carrying scroll-snap-align:start,
      six 250px columns in a 1056px window. Everything a rail provides by
      way of scrolling was already there - what was missing was any way to
      ask for it. The scrollbar is hidden and there were no arrows and no
      drag, so the sixth card could not be reached at all.

      Wrapping it would have nested one scroller inside another. So when
      opts.adopt is given, that element becomes the track as it stands and
      the rail contributes only the controls.
    */
    this.adopt = opts.adopt || null;
    this.tileSelector = opts.tile || TILE_DEFAULT;
  }

  Rail.prototype.build = function () {
    if (this.built) return;
    this.built = true;
    var self = this;

    if (this.adopt) {
      // Already a scroller; take it as the track and add nothing to the box.
      this.track = this.adopt;
      this.track.classList.add('bc-rail-adopted');
      this.track.setAttribute('tabindex', '0');
      this.track.setAttribute('role', 'group');
    } else {
      this.row.classList.add('bc-rail-row');
      this.track = document.createElement('div');
      this.track.className = 'bc-rail-track';
      this.track.setAttribute('tabindex', '0');
      this.track.setAttribute('role', 'group');
      while (this.row.firstChild) this.track.appendChild(this.row.firstChild);
      this.row.appendChild(this.track);
    }

    this.nav = document.createElement('div');
    this.nav.className = 'bc-rail-nav';
    this.prev = arrow('prev');
    this.next = arrow('next');
    this.nav.appendChild(this.prev);
    this.nav.appendChild(this.next);
    if (this.headerSlot) {
      // Beside "View all", where the row's own controls belong.
      this.headerSlot.parentNode.insertBefore(this.nav, this.headerSlot);
    } else {
      // A second overflowing row in the same section has no header of its
      // own, so its arrows ride the row instead of stealing the first
      // row's ones.
      this.nav.classList.add('bc-rail-nav--inline');
      this.row.insertBefore(this.nav, this.track);
    }

    this.prev.addEventListener('click', function () { self.step(-1); });
    this.next.addEventListener('click', function () { self.step(1); });
    this.track.addEventListener('scroll', function () { self.onScroll(); }, { passive: true });
    this.track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); self.step(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); self.step(-1); }
    });
    // Shift+wheel is the mouse's horizontal gesture; a trackpad sends
    // deltaX on its own. Claimed only while the rail can still move that
    // way, so a rail at its end does not swallow the page's scroll.
    this.track.addEventListener('wheel', function (e) {
      var d = e.deltaX || (e.shiftKey ? e.deltaY : 0);
      if (!d) return;
      var max = self.track.scrollWidth - self.track.clientWidth;
      if ((d < 0 && self.track.scrollLeft <= 0) || (d > 0 && self.track.scrollLeft >= max)) return;
      e.preventDefault();
      self.track.scrollLeft += d;
    }, { passive: false });

    this.disarmNativeDrag();
    this.bindDrag();
    this.syncColumn();
    this.relayout();
  };

  Rail.prototype.disarmNativeDrag = function () {
    /*
      draggable="false" on everything in the track that the browser would
      otherwise pick up. The CSS carries -webkit-user-drag:none for the
      same reason, but the attribute is what older engines read, and the
      lightbox swaps <img> src in place - so this runs again whenever the
      track's contents change rather than once at build.
    */
    var track = this.track;
    var disarm = function () {
      var nodes = track.querySelectorAll('a, img');
      for (var i = 0; i < nodes.length; i++) nodes[i].setAttribute('draggable', 'false');
    };
    disarm();
    if (window.MutationObserver) {
      this.mo = new MutationObserver(disarm);
      this.mo.observe(track, { childList: true, subtree: true });
    }
  };

  Rail.prototype.syncColumn = function () {
    /*
      An adopted track sizes its own columns, so it needs no --bc-rail-col.
      It still needs WATCHING: whether it overflows depends on its width,
      and refresh() is what turns that into the arrows' state. Without an
      observer the adopted rail only re-evaluated on window resize, and a
      lane can change width with the window still - the cloth block is
      fetched in after load, images land, a scrollbar appears.
    */
    if (this.adopt) {
      if (!this.ro && window.ResizeObserver) {
        var adopted = this;
        this.ro = new ResizeObserver(function () {
          if (adopted.built) adopted.relayout();
        });
        this.ro.observe(this.track);
      }
      return;
    }
    /*
      A rail card must measure exactly what a grid card measures, and the
      track cannot work that out on its own: the row carries 2px of inline
      lane padding, so the track's own width is 1048 where the strip's
      content box is 1052, and four columns of 25% come out a pixel narrow.

      So the width is taken from the grid rather than recomputed - the
      strip's first column track, read straight off the computed style. By
      construction rather than by arithmetic that has to be kept in step.
    */
    var strip = this.row.parentElement;
    if (!strip) return;
    var cols = window.getComputedStyle(strip).gridTemplateColumns;
    if (!cols) return;
    var first = parseFloat(cols.split(' ')[0]);
    if (first > 0) this.track.style.setProperty('--bc-rail-col', first + 'px');

    /*
      Watch the strip rather than the window. The column has to follow the
      grid whenever the grid changes, and a window resize is not the only
      thing that changes it - measured at 1920, where the grid went to
      406.61px and the rail stayed on the 255.5 it was built with, because
      the resize event the listener waits for never arrived. A
      ResizeObserver on the strip cannot miss it.
    */
    if (!this.ro && window.ResizeObserver) {
      var self = this;
      this.ro = new ResizeObserver(function () {
        if (!self.built) return;
        var c = window.getComputedStyle(strip).gridTemplateColumns;
        var v = c ? parseFloat(c.split(' ')[0]) : 0;
        if (v > 0) self.track.style.setProperty('--bc-rail-col', v + 'px');
        self.relayout();
      });
      this.ro.observe(strip);
    }
  };

  Rail.prototype.bindDrag = function () {
    /*
      Drag to scroll, which is how a rail is actually used - the arrows are
      the secondary control.

      Mouse only: touch already scrolls the track natively and hijacking it
      would be worse than doing nothing. The class is added on the first
      real movement rather than on pointerdown, because a press that never
      moves is a click on a card, and a rail that swallowed those would
      break every tile in the row.

      DRAG_SLOP is the line between the two. Past it the drag owns the
      gesture and the click that the browser fires on release is swallowed
      once, in the capture phase, so a swipe cannot open a product.
    */
    var DRAG_SLOP = 6;
    var self = this, track = this.track;
    var down = false, dragging = false, startX = 0, startScroll = 0;

    /*
      Capture phase, all of it.

      A tile is covered by its own absolutely positioned link and carries
      the lightbox's and the touch-active observer's handlers, any of which
      may stop a pointer event before it bubbles to the track. Listening on
      the way DOWN means the rail sees every press first, whatever a
      descendant does with it afterwards - which is why the drag did
      nothing while the arrows worked.

      Once past the slop the move is captured to the track, so the gesture
      survives the pointer leaving the rail mid-drag.
    */
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      down = true; dragging = false;
      startX = e.clientX; startScroll = track.scrollLeft;
      /*
        Snap off HERE, inline, on the press - not on the first move via a
        class.

        This is what made the row twitch and die. Mandatory snap re-snaps
        every scrollLeft the drag writes, and adding .is-dragging in the
        same tick as the first assignment does not help: the class has not
        been applied to the computed style yet when scrollLeft is set, so
        that first write is snapped straight back. Measured on a real
        mouse drag - the pointer moved 70px then 141, and scrollLeft ended
        at 22.

        An inline property beats the stylesheet and takes effect at once,
        and doing it on the press means no write is ever unprotected. The
        class stays for the cursor and user-select.
      */
      track.style.setProperty('scroll-snap-type', 'none', 'important');
      track.style.setProperty('scroll-behavior', 'auto', 'important');
    }, true);

    track.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_SLOP) return;
        dragging = true;
        track.classList.add('is-dragging');
        if (track.setPointerCapture) { try { track.setPointerCapture(e.pointerId); } catch (err) {} }
      }
      // The press has become a drag: nothing downstream should also act on
      // it, and the browser should not start its own image drag either.
      e.preventDefault();
      track.scrollLeft = startScroll - dx;
    }, true);

    function release() {
      if (!down) return;
      down = false;
      // The press always restores what the press turned off, drag or not.
      track.style.removeProperty('scroll-snap-type');
      track.style.removeProperty('scroll-behavior');
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      /*
        Land on a card deliberately rather than hoping the engine re-snaps
        when snap comes back. Re-enabling scroll-snap-type does not
        re-position a scroller that is already at rest in every engine, so
        the row could stop between two cards - which is the one thing the
        rail must never do.
      */
      self.snapToNearest();
      var swallow = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
      track.addEventListener('click', swallow, true);
      window.setTimeout(function () { track.removeEventListener('click', swallow, true); }, 0);
      self.refresh();
    }

    track.addEventListener('pointerup', release, true);
    track.addEventListener('pointercancel', release, true);
    /*
      NOT pointerleave. Taking the pointer capture re-targets boundary
      events, so the browser fires pointerleave on the track the moment
      capture is granted - which ended the drag on its own first move.
      Measured: pointerdown@397, pointermove@297 followed to scrollLeft
      100, and the next move at @197 was ignored because `down` had
      already been cleared. That is the drag moving once and stopping.

      Capture is what makes it unnecessary anyway: pointerup is delivered
      to the track even when the pointer is released outside it, which is
      also what keeps a drag alive when the mouse leaves the rail.
    */
    /*
      The native drag is what actually broke this, and the guard against it
      was written wrong: dragstart was cancelled only `if (dragging)`, and
      the browser fires dragstart BEFORE the 6px slop has set that flag. So
      it was never cancelled, the pointer became an HTML5 link/image drag -
      every tile is an <a> full of <img> - and pointermove stopped arriving
      the moment the ghost picked up. Synthetic events passed because they
      never go near that machinery.

      Cancelled unconditionally now: nothing inside a rail is draggable.
    */
    track.addEventListener('dragstart', function (e) { e.preventDefault(); }, true);
    track.addEventListener('drag', function (e) { e.preventDefault(); }, true);
  };

  Rail.prototype.teardown = function () {
    if (!this.built) return;
    this.built = false;
    this.row.classList.remove('bc-rail-row');
    if (this.nav && this.nav.parentNode) this.nav.parentNode.removeChild(this.nav);
    if (this.adopt) {
      // Borrowed, not built: hand it back as it was - including the gap and
      // the width the whole-card fit wrote on it, which belong to the rail
      // and not to the phone's swipe lane below 750.
      this.track.classList.remove('bc-rail-adopted');
      this.track.removeAttribute('tabindex');
      this.track.removeAttribute('role');
    }
    if (this.track) {
      this.track.style.removeProperty('column-gap');
      this.track.style.removeProperty('max-width');
      this.track.style.removeProperty('--bc-rail-card');
    }
    if (!this.adopt && this.track && this.track.parentNode === this.row) {
      while (this.track.firstChild) this.row.insertBefore(this.track.firstChild, this.track);
      this.row.removeChild(this.track);
    }
    if (this.ro) { this.ro.disconnect(); this.ro = null; }
    if (this.mo) { this.mo.disconnect(); this.mo = null; }
    this.nav = this.track = null;
  };

  Rail.prototype.snapToNearest = function () {
    // Nearest card start to where the drag left the track, clamped to the
    // scrollable range so the last page does not overshoot.
    var track = this.track;
    // A drag can be released after the rail has been torn down - crossing
    // below 750px mid-gesture does exactly that, and teardown nulls the
    // track. Reading scrollLeft off null then throws inside a pointerup
    // handler, which kills the rest of the release.
    if (!this.built || !track) return;
    var tiles = tilesOf(track, this.tileSelector);
    if (!tiles.length) return;
    var base = tiles[0].getBoundingClientRect().left + track.scrollLeft;
    var max = track.scrollWidth - track.clientWidth;
    var at = track.scrollLeft, best = 0, bestGap = Infinity;
    for (var i = 0; i < tiles.length; i++) {
      var offset = Math.min(max, Math.max(0, tiles[i].getBoundingClientRect().left + at - base));
      var gap = Math.abs(offset - at);
      if (gap < bestGap) { bestGap = gap; best = offset; }
    }
    if (bestGap > 0.5) track.scrollTo({ left: best, behavior: 'smooth' });
    var self = this;
    window.setTimeout(function () { self.refresh(); }, 420);
  };

  Rail.prototype.pitch = function () {
    // One card, measured rather than assumed: the gap belongs to the step.
    var t = tilesOf(this.track, this.tileSelector);
    if (t.length < 2) return this.track.clientWidth;
    var a = t[0].getBoundingClientRect(), b = t[1].getBoundingClientRect();
    return (b.left > a.left) ? (b.left - a.left) : a.width;
  };

  /*
    WHOLE CARDS, NEVER A SLICE (2026-09-23, the owner's brief on the ball
    sets row). A lane is as wide as the container, and a container is not a
    whole number of cards wide: at 1920 the ball sets lane held six 250px
    cards and 95px of the seventh, which read as a card cut in half at the
    right edge. Measured the same day: 110px of a cue tip card at 1280, 180
    of a chalk card, 10 of a ball set.

    So the lane is fitted to a whole number of cards. The leftover goes
    into the GAPS first - the brief's own first choice - but a gap may only
    stretch to twice its design value, or a row of three would stand 100px
    apart; whatever is still left shortens the lane itself, so the rail
    ends where its last whole card ends and nothing is sliced. The step
    below then moves one full page, so every stop lands on whole cards too.

    The room is read from the lane's PARENT, never from the lane, because
    the lane's own width is what this changes - reading it back would make
    each pass narrow the row again.
  */
  var GAP_STRETCH = 2;

  Rail.prototype.fit = function () {
    var track = this.track;
    if (!track) return;
    /*
      ADOPTED LANES ONLY - the rows whose cards are a fixed width (ball
      sets 250, cue tips 225, chalk 280) and whose lane is its own
      scroller. The cue and Longoni strips take their card width FROM the
      grid they sit in (--bc-rail-col, syncColumn above), so narrowing
      their track narrows that grid, which re-deals narrower columns, which
      narrows the track again: measured at 1280, a 1040px lane at 704 and
      339px cards at 226. They also never slice a card - their grid already
      deals a whole number of columns, and the 3 to 5px at the right edge
      is the lane's own padding.
    */
    if (!this.adopt || this.noFit) return;
    var tiles = tilesOf(track, this.tileSelector);
    if (tiles.length < 2) return;

    /*
      Every pass starts from the row the stylesheet drew. Without this the
      fit read back its own result: the cue strips' row is sized BY the
      track, so a narrowed track narrowed its parent, the next pass read
      that narrower parent and narrowed it again - measured at 1280, a
      1040px lane down to 704 in two passes, three cards to two.
    */
    track.style.removeProperty('column-gap');
    track.style.removeProperty('max-width');
    track.style.removeProperty('--bc-rail-card');

    var cs = window.getComputedStyle(track);
    var padL = parseFloat(cs.paddingLeft) || 0;
    var padR = parseFloat(cs.paddingRight) || 0;
    var a = tiles[0].getBoundingClientRect();
    var b = tiles[1].getBoundingClientRect();
    var card = a.width;
    if (card < 1) return;
    if (this.designGap == null) {
      var measured = (b.left > a.left ? b.left - a.left : card) - card;
      this.designGap = measured > 0 ? measured : 0;
    }

    var host = track.parentElement;
    var room = (host ? host.clientWidth : track.clientWidth) - padL - padR;
    if (room < card) return;

    var gap = this.designGap;
    var n = Math.floor((room + gap) / (card + gap));
    if (n < 1) n = 1;

    /*
      A LANE MAY ASK FOR FLUID CARDS (2026-09-23, the owner's "seven cards"
      brief). data-rail-card-min names the narrowest card the row accepts;
      the rail then fits as many of those as the room holds and widens them
      back out to fill it exactly, so the row ends on the container's edge
      with no slack and no sliced card. The width is published as
      --bc-rail-card and the section's own stylesheet reads it.

      Only a lane that carries the attribute is touched. Every other row
      keeps the width its stylesheet gives it.
    */
    var fluidMin = parseFloat(track.getAttribute('data-rail-card-min')) || 0;
    if (fluidMin > 0) {
      var fluidN = Math.floor((room + gap) / (fluidMin + gap));
      if (fluidN > tiles.length) fluidN = tiles.length;
      if (fluidN < 1) fluidN = 1;
      var fluidW = (room - (fluidN - 1) * gap) / fluidN;
      if (fluidW < fluidMin) fluidW = fluidMin;
      track.style.setProperty('--bc-rail-card', fluidW + 'px');
      card = fluidW;
      n = fluidN;
    }

    if (n >= tiles.length) {
      // Everything fits: leave the row exactly as the stylesheet drew it.
      track.style.removeProperty('column-gap');
      track.style.removeProperty('max-width');
      this.perView = tiles.length;
      this.cardPitch = card + gap;
      return;
    }

    if (n > 1) {
      var spread = (room - n * card) / (n - 1);
      gap = spread > this.designGap * GAP_STRETCH ? this.designGap * GAP_STRETCH : spread;
      if (gap < this.designGap) gap = this.designGap;
    }
    var used = n * card + (n - 1) * gap;

    track.style.setProperty('column-gap', gap + 'px');
    // max-width, not a margin: these lanes carry width:100%, and a margin
    // would push the box out of its row instead of shortening it.
    track.style.setProperty('max-width', (used + padL + padR) + 'px');
    this.perView = n;
    this.cardPitch = card + gap;

    /*
      THE LANE MUST NOT SIZE ITS OWN CARDS. Some adopted lanes deal their
      columns as fractions of themselves - the Championship cloth lane and
      the two case rows - so shortening the lane makes the cards narrower,
      which makes the fit want to shorten it again. Measured at 1280:
      Championship cards 199 down to 163, the case rows 339 down to 226.

      So the result is read back once. If the card moved, this lane is one
      of those: the fit is undone and never tried again on it, and the row
      keeps the lane the stylesheet drew. The page step still counts whole
      cards.
    */
    var settled = tilesOf(track, this.tileSelector)[0];
    var now = settled ? settled.getBoundingClientRect().width : card;
    if (Math.abs(now - card) > 1) {
      track.style.removeProperty('column-gap');
      track.style.removeProperty('max-width');
      this.noFit = true;
      this.cardPitch = card + this.designGap;
    }
  };

  /*
    ONE CLICK IS ONE SCREENFUL (2026-10-12, the owner's brief).

    The step was cardPitch * (this.perView || 1), and perView is set in ONE
    place: fit(), which returns at its first line for every rail that is not
    an adopted lane and for every lane whose fit was undone. So on those the
    fallback did the work and a click moved exactly one card. Measured on the
    homepage at 1920, one click: the four McDermott rows, Longoni Pool Shafts,
    Cue Tips and Glove Cards all moved 1 card of the 4 to 7 that were on
    screen; Pool Ball Sets and Carom Sets, whose fit does run, moved their
    whole 7; Longoni Cue Cases moved 3 of 4.

    The count is measured here instead, at click time, from the lane and the
    cards as they stand - so it is right on a rail the fit never touched, and
    right again after a resize without anything having to be recomputed.

    THE REST OF THE RULE IS IN THE TARGET, not the distance. Stepping by a
    distance keeps whatever fraction of a card the rail was resting on; this
    walks the CARDS - find the one nearest the left edge, take the one n
    along, put its left edge on the lane's - so every stop is a card boundary
    whatever came before it, including a swipe that left the rail mid-card.
    Clamped to the scrollable range at both ends, which is what makes the last
    page sit flush against the end instead of overshooting into empty space.

    scrollTo rather than scrollBy, and still with no behavior of its own: the
    track carries scroll-behavior in CSS, so the easing is the one it had and
    a prefers-reduced-motion rule can still switch it off.
  */
  Rail.prototype.pageCount = function () {
    var track = this.track;
    if (!track) return 1;
    var pitch = this.cardPitch || this.pitch();
    if (!(pitch > 0)) return 1;
    var tiles = tilesOf(track, this.tileSelector);
    var card = tiles.length ? tiles[0].getBoundingClientRect().width : pitch;
    var gap = pitch - card;
    var cs = window.getComputedStyle(track);
    var room = track.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
    // n cards occupy n*card + (n-1)*gap, so the count is (room + gap) / pitch
    var count = Math.floor((room + gap + 0.5) / pitch);
    if (count < 1) count = 1;
    if (tiles.length && count > tiles.length) count = tiles.length;
    return count;
  };

  Rail.prototype.step = function (dir) {
    var track = this.track;
    var tiles = tilesOf(track, this.tileSelector);
    var max = track.scrollWidth - track.clientWidth;
    var count = this.pageCount();
    var target;

    if (tiles.length > 1) {
      var cs = window.getComputedStyle(track);
      var padL = parseFloat(cs.paddingLeft) || 0;
      var base = track.getBoundingClientRect().left;
      var offs = [];
      for (var i = 0; i < tiles.length; i++) {
        offs.push(tiles[i].getBoundingClientRect().left - base + track.scrollLeft - padL);
      }
      var cur = 0;
      var best = Infinity;
      for (var j = 0; j < offs.length; j++) {
        var away = Math.abs(offs[j] - track.scrollLeft);
        if (away < best) { best = away; cur = j; }
      }
      var want = cur + count * dir;
      if (want < 0) want = 0;
      if (want > offs.length - 1) want = offs.length - 1;
      target = offs[want];
    } else {
      target = track.scrollLeft + track.clientWidth * dir;
    }

    if (target < 0) target = 0;
    if (target > max) target = max;
    track.scrollTo({ left: target });

    var self = this;
    window.setTimeout(function () { self.refresh(); }, 0);
    window.setTimeout(function () { self.refresh(); }, 420);
  };

  Rail.prototype.onScroll = function () {
    var self = this;
    if (this.frame) return;
    this.frame = requestAnimationFrame(function () { self.frame = 0; self.refresh(); });
  };

  /*
    The fit is a LAYOUT question, so it runs when the layout changes - on
    build and on a resize - and never from refresh(), which runs on every
    scroll frame. Resetting and re-measuring the lane sixty times a second
    is both wasted work and a way to fight the scroll that is in progress.
  */
  Rail.prototype.relayout = function () {
    this.fit();
    this.refresh();
  };

  Rail.prototype.refresh = function () {
    if (!this.built || !this.track || !this.nav) return;
    var max = this.track.scrollWidth - this.track.clientWidth;
    var at = this.track.scrollLeft;
    /*
      NOTHING TO SCROLL MEANS NO CONTROLS.

      A rail is built from the tile COUNT - more tiles than fit across at
      the width it was built at - but whether the track actually overflows
      is a question of WIDTH, and the answer changes under the row. Five
      202px cards overflow a 900px lane and fit a 1100px one.

      Without this the row kept a pair of arrows it could not use: max is
      0, so atStart and atEnd are BOTH true, both arrows are disabled, and
      a press does nothing. Reported as "the arrows do not work"; measured
      on the Championship Cloth row at a 1920 viewport, scrollWidth ===
      clientWidth with the nav still on screen. Drag reads as broken in
      the same state for the same reason - there is nowhere to drag to.

      Hidden rather than removed, so the same rail picks them back up when
      the viewport narrows and the track overflows again.
    */
    this.nav.hidden = max <= 1;
    var atStart = at <= 1, atEnd = at >= max - 1;
    this.prev.classList.toggle('is-spent', atStart);
    this.next.classList.toggle('is-spent', atEnd);
    this.prev.disabled = atStart;
    this.next.disabled = atEnd;
  };

  function apply(mq) {
    rails.forEach(function (r) { if (mq.matches) r.build(); else r.teardown(); });
  }

  function init() {
    // [data-mcdermott-tiles] rather than the mcdermott-tiles section id. The
    // id is unique per instance, so a second McDermott rail matched nothing
    // here and silently got no arrows - the trap that breaks slideshow.liquid
    // when it is duplicated. The attribute already marks every instance;
    // longoni-lightbox.js selects on it the same way.
    // The two Longoni hosts by name: their heading lives in the host and
    // their tiles arrive in a fetched section inside it, so only the host
    // holds both the row and the View all the arrows hang beside.
    var sections = document.querySelectorAll('[data-mcdermott-tiles], #shopify-section-longoni-cues, #shopify-section-longoni-carom');
    Array.prototype.forEach.call(sections, function (section) {
      var heading = section.querySelector('.bc-row-heading');
      var viewAll = heading ? heading.querySelector('.bc-row-heading__view-all') : null;
      var headerTaken = false;
      Array.prototype.forEach.call(section.querySelectorAll('.longoni-promo-strip__row'), function (row) {
        if (row.dataset.bcRail) return;
        // Only a row that actually overflows gets the treatment.
        if (tilesOf(row).length <= PER_VIEW) return;
        row.dataset.bcRail = '1';
        var slot = (!headerTaken && viewAll) ? viewAll : null;
        if (slot) headerTaken = true;
        rails.push(new Rail(row, section, slot));
      });
    });
    /*
      Rows that already scroll and only need controls. They opt in from
      Liquid with data-bc-rail-adopt on the scroller itself, rather than
      being found by class: the cloth snippet renders Simonis and
      Championship through the same markup, and only Championship overflows
      and needs this.

      The heading sits just before the lane's wrapper, so the arrows go
      beside that row's own View all, exactly where the cue rows put them.
    */
    Array.prototype.forEach.call(document.querySelectorAll('[data-bc-rail-adopt]'), function (scroller) {
      if (scroller.dataset.bcRailBuilt) return;
      var wrap = scroller.parentElement || scroller;
      var heading = null, probe = wrap;
      while (probe && !heading) {
        probe = probe.previousElementSibling;
        if (probe && probe.classList && probe.classList.contains('bc-row-heading')) heading = probe;
      }
      var viewAll = heading ? heading.querySelector('.bc-row-heading__view-all') : null;
      var tiles = tilesOf(scroller, '.bc-card');
      if (tiles.length <= PER_VIEW) return;
      scroller.dataset.bcRailBuilt = '1';
      rails.push(new Rail(wrap, wrap, viewAll, { adopt: scroller, tile: '.bc-card' }));
    });

    if (!rails.length) return;
    var mq = window.matchMedia(DESKTOP);
    apply(mq);
    /*
      Once, however many times init() runs.

      init() is re-entrant by design - a lazily fetched row calls it again
      when it arrives - and it used to add a fresh matchMedia and resize
      listener on every pass. Two rows fetched separately meant every
      resize ran the whole rail set twice, and every listener kept the
      closure alive after teardown.
    */
    if (!wired) {
      wired = true;
      if (mq.addEventListener) mq.addEventListener('change', function () { apply(mq); });
      else if (mq.addListener) mq.addListener(function () { apply(mq); });
      window.addEventListener('resize', function () {
        rails.forEach(function (r) { if (r.built) { r.syncColumn(); r.relayout(); } });
      }, { passive: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  /*
    shopify:section:load is a THEME EDITOR event. It does not fire on the
    storefront, so a row that arrives through the Section Rendering API has
    to say so itself - sections/cloth-rows-host.liquid calls
    window.BCDesktopRail.init() straight after it injects. The listener
    stays for the editor, where it is the only signal there is.

    init() is idempotent: a built rail carries data-bc-rail-built, an
    adopted scroller is skipped on sight, and the globals wire once.
  */
  document.addEventListener('shopify:section:load', function () { init(); });
  window.BCDesktopRail = { init: init };
})();




